import React from 'react';
import {TextField, view} from '@sail/ui';
import {useIntl} from '@sail/intl';
import type {OpenapiAccountTypeEnum} from '@stripe-internal/data-v1/schema/types';
import {FormExplanation} from './FormExplanation';
import {ExternalAccountConfirmationFooter} from './ExternalAccountConfirmationFooter';
import {externalAccountMessages} from './ExternalAccountConfirmation';
import {PreviousExternalAccountNumber} from './PreviousExternalAccountNumber';
import type {BankAccountExternalAccountFragmentData} from '../queries/bankAccountExternalAccountFragment';

export type Props = {
  onSave: (accountNumber: string) => Promise<void>;
  onClose: () => void;
  error?: string;
  identityVerificationEnabled?: boolean;
  onVerifyIdentity?: () => void;
  sending: boolean;
  externalAccount: BankAccountExternalAccountFragmentData;
  accountType?: OpenapiAccountTypeEnum;
};

export const BankAccountVerification = ({
  onSave,
  onClose,
  error: externalError,
  identityVerificationEnabled,
  onVerifyIdentity,
  sending,
  externalAccount,
  accountType,
}: Props) => {
  const intl = useIntl();
  const [accountNumber, setAccountNumber] = React.useState<string>('');

  const isButtonDisabled = sending || accountNumber.trim().length === 0;

  return (
    <view.form
      onSubmit={(e) => {
        e.preventDefault();
        // onSave can take a token or a raw account number, tokenization was removed from bank account verification intentionally because it fails for deny-listed bank accounts
        // bank accounts are deny-listed automatically on failed payout, so it's important we don't make changing the bank account after failed payout impossible
        // https://confluence.corp.stripe.com/display/T2RC/Blocklisted+Bank+Accounts+-+ACH+Debit+Declines+and+Failed+Payouts
        // Tokenization of bank accounts is unnecessary for stripe-surfaces, since we own this frontend surface and are not storing this data on the client
        onSave(accountNumber);
      }}
      css={{stack: 'y', gap: 'large', font: 'body.small'}}
    >
      <FormExplanation
        identityVerificationEnabled={identityVerificationEnabled}
        accountType={accountType}
        onVerifyIdentity={onVerifyIdentity}
      />
      <view.div css={{stack: 'y', gap: 'medium'}}>
        <PreviousExternalAccountNumber externalAccount={externalAccount} />
        <TextField
          label={intl.formatMessage(
            externalAccountMessages.externalAccountField,
          )}
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value.replace(/\s/g, ''))}
          error={externalError}
          name="confirm_previous_account_number"
        />
      </view.div>
      <ExternalAccountConfirmationFooter
        sending={sending}
        onClose={onClose}
        disabled={isButtonDisabled}
      />
    </view.form>
  );
};
